/**
 * The Company Data Export: what the archive is, and how each value is written.
 *
 * The archive is a ZIP of CSV files, one per kind of record, plus a
 * `manifest.json` that says what is in it and a `README.txt` that says it in
 * words. It is for portability and for recovery *assistance* — a person, or
 * another system, can read every record back out of it — and it is not a
 * database backup: Timely Payment cannot yet import it (08 §13).
 *
 * The cell rules are the product's CSV rules (`csv.ts`), unchanged:
 *
 *  - UTF-8 with a byte order mark, CRLF line endings, RFC 4180 quoting;
 *  - a **text** value beginning with `=`, `+`, `-`, `@`, a tab or a carriage
 *    return is prefixed with an apostrophe, so a customer name typed as a
 *    formula is shown, not run;
 *  - **numbers are never prefixed**: `-1500` stays a number;
 *  - a JSON-valued column (ageing buckets, notes, audit details) is written as
 *    JSON text in one cell, so its structure survives;
 *  - true/false as `true`/`false`, empty for nothing.
 *
 * Relationships survive through ids: an activity's `customer_id` is the `id`
 * of a row in customers.csv, a member's `role_id` a row in roles.csv. Nothing
 * else internal is written: no company id (every row is this company's), no
 * sign-in account id, and never a credential.
 */
import { numberCell, textCell } from './csv.js';

export const COMPANY_EXPORT_FORMAT = 'timely-payment/company-export';
export const COMPANY_EXPORT_FORMAT_VERSION = 1;

/** One exported file, as the database planned it. */
export interface ExportFilePlan {
    entity: string;
    file: string;
    columns: string[];
    rows: number;
}

/** One value, as a CSV cell. */
export function exportCell(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return numberCell(value);
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'string') return textCell(value);
    // Objects and arrays: their JSON, protected and quoted like any text.
    return textCell(JSON.stringify(value));
}

const BOM = '﻿';

/** The first line of a file: the byte order mark and the column names. */
export const exportCsvHeader = (columns: readonly string[]): string =>
    `${BOM}${columns.map(c => textCell(c)).join(',')}\r\n`;

/** One record, in the file's column order. A column the row lacks is empty. */
export const exportCsvRow = (columns: readonly string[], data: Readonly<Record<string, unknown>>): string =>
    `${columns.map(c => exportCell(data[c])).join(',')}\r\n`;

/** What the company is called in a file name: letters, digits and hyphens. */
export function exportFileName(slug: string, day: string): string {
    const clean = slug.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'company';
    return `company-export-${clean}-${day.slice(0, 10)}.zip`;
}

export interface ExportManifestInput {
    exportId: string;
    generatedAt: string;
    snapshotAt: string;
    company: { name: string; slug: string };
    schemaVersion: string;
    files: readonly ExportFilePlan[];
    excluded: readonly { entity: string; reason: string }[];
    logo: { included: boolean; file: string | null; bytes: number | null; note: string | null };
}

/** `manifest.json`: machine-readable, self-describing, and free of secrets. */
export function buildExportManifest(input: ExportManifestInput): Record<string, unknown> {
    const counts: Record<string, number> = {};
    for (const f of input.files) counts[f.entity] = f.rows;
    return {
        format: COMPANY_EXPORT_FORMAT,
        format_version: COMPANY_EXPORT_FORMAT_VERSION,
        application: 'Timely Payment',
        schema_version: input.schemaVersion,
        export_id: input.exportId,
        generated_at: input.generatedAt,
        snapshot_at: input.snapshotAt,
        company: input.company,
        consistency: 'Every file was copied from the database in a single statement, so all of them show the company as it was at snapshot_at.',
        restore: 'This export is for portability and recovery assistance. Timely Payment cannot yet import it automatically.',
        encoding: 'UTF-8 CSV with a byte order mark, CRLF line endings and RFC 4180 quoting. Text beginning with = + - @ tab or carriage return is prefixed with an apostrophe; numbers never are. JSON-valued columns hold JSON text. Booleans are true/false; an empty cell means no value.',
        files: input.files.map(f => ({ file: f.file, entity: f.entity, rows: f.rows, columns: f.columns })),
        counts,
        logo: input.logo,
        excluded: input.excluded,
        never_included: [
            'AI provider keys, Google tokens and every other stored credential (only whether each is configured)',
            'encrypted secret material and master encryption keys',
            'invitation tokens, sign-in state and passwords',
            'sign-in account ids',
        ],
    };
}

/** `README.txt`: the same thing, for a person. */
export function buildExportReadme(input: { companyName: string; snapshotAt: string; files: readonly ExportFilePlan[] }): string {
    const lines = [
        `Company Data Export — ${input.companyName}`,
        '',
        `Taken from Timely Payment at ${input.snapshotAt} (UTC). Every file shows the company at that same moment.`,
        '',
        'What this is',
        '  A copy of your company\'s records you can open in a spreadsheet or load into another system.',
        '  It is for portability and recovery assistance. Timely Payment cannot yet import it automatically.',
        '',
        'What is not in it',
        '  No keys, tokens, passwords or other credentials. For each stored key it says only whether one is configured.',
        '',
        'Files',
        ...input.files.map(f => `  ${f.file.padEnd(24)} ${String(f.rows).padStart(8)} rows`),
        '',
        'How the files are written',
        '  UTF-8 CSV. Text that begins with = + - or @ is prefixed with an apostrophe so a spreadsheet shows it rather than running it.',
        '  Columns holding structured values (ageing, notes, audit details) contain JSON text.',
        '  Rows refer to each other by id: activity.csv customer_id is an id in customers.csv; members.csv role_id is an id in roles.csv.',
        '  manifest.json lists every file, its columns and its row count.',
        '',
    ];
    return lines.join('\r\n');
}

/**
 * The words for each kind of record, for the Fresh Start preview and the
 * deletion dialog. Keyed by table name, as the database reports counts.
 * Absent: bookkeeping a person has no reason to see counted.
 */
export const LIFECYCLE_LABELS: Readonly<Record<string, string>> = Object.freeze({
    customers: 'Accounts',
    customer_activity: 'History entries',
    promises: 'of which promises',
    pdc_cheques: 'Cheques',
    sync_runs: 'Import runs',
    ai_report_runs: 'AI summary records',
    workspace_members: 'Team members',
    workspace_roles: 'Roles',
    workspace_invites: 'Invitations',
    workspace_secrets: 'Stored keys and connections (kept sealed)',
    workspace_integrations: 'Data source and stock sheet settings',
    workspace_alert_settings: 'Daily email settings',
    workspace_categories: 'Customer categories',
    message_templates: 'Message templates',
    workspace_settings: 'Company profile',
    workspace_owner_mappings: 'Owner code mappings',
    audit_log: 'Audit log entries',
});
